import {
    Component, Mixins, Watch,
} from 'vue-property-decorator';
import CoreMixin from '@nodes/mixins/Core';
import { digits, separated, percent } from '@nodes/helpers/number';
import { date } from '@nodes/helpers/date';
import {
    YPanel, YTable, YImage, YBadge, YEmpty,
} from '@components/index';
import HybridMixin from '@rogues/shared/mixins/hybrid';
import Props from './props';

@Component({
    components: {
        YPanel,
        YTable,
        YImage,
        YBadge,
        YEmpty,
    },
})

/**
 * @name ProfileOrders
 */
export default class ProfileOrders extends Mixins(CoreMixin, HybridMixin, Props) {
    digits = digits;
    separated = separated;
    date = date;
    percent = percent;

    selectedFilter: string = 'all';
    orders = null;
    orderDetails = null;

    tableHeader = [
        {
            id   : 'order_number',
            title: this.$t('dashboard.orders.table.order_number'),
        },
        {
            id   : 'date',
            title: this.$t('dashboard.orders.table.date'),
        },
        {
            id   : 'time',
            title: this.$t('dashboard.orders.table.time'),
        },
        {
            id   : 'status',
            title: this.$t('dashboard.orders.table.status'),
        },
        {
            id   : 'price',
            title: this.$t('dashboard.orders.table.price'),
        },
        {
            id   : 'details',
            title: this.$t('dashboard.orders.table.details'),
        },
    ];

    transactionsTableHeader = [
        {
            id   : 'date',
            title: this.$t('dashboard.transactions.table.date'),
        },
        {
            id   : 'price',
            title: this.$t('dashboard.transactions.table.price'),
        },
        {
            id   : 'status',
            title: this.$t('dashboard.transactions.table.status'),
        },
        {
            id   : 'back_track_no',
            title: this.$t('dashboard.transactions.table.back_track_no'),
        },
    ]

    /**
     * Check if it is details view of the order
     * 
     * @returns {boolean}
     */
    get isDetails(): boolean {
        return !!this.$route.params.id;
    }

    /**
     * Return the filtered transactions
     * 
     * @returns {Array}
     */
    get filteredOrders(): Array<{[k: string]: any}> {
        let list = [];

        if (this.selectedFilter === 'all') {
            list = this.orders;
        } else if (this.selectedFilter === 'open') {
            list = this.orders.filter((i) => i.is_closed === false);
        } else if (this.selectedFilter === 'close') {
            list = this.orders.filter((i) => i.is_closed === true);
        }
        return list;
    }

    /**
     * Fetch Orders
     * 
     * @returns {*}
     */
    fetchOrders(): any {
        this.$wait.start('fetch-orders');
        const params = {
            including: ['is_closed', 'created_at', 'effected_at', 'paid_amount', 'payable_amount', 'status_title', 'total_price', 'tracking_number', 'currency'],
        };
        return this.$services[this.fetchOrdersService.name][this.fetchOrdersService.api](params).then((response) => {
            this.orders = response.data.results;
        }).catch((error: object) => {
            this.handleError(error);
        }).finally(() => {
            this.$wait.end('fetch-orders');
        });
    }

    /**
     * Fetch Order Details
     * 
     * @returns {*}
     */
    fetchOrderDetails(): any {
        this.$wait.start('fetch-order-details');
        const params = {
            id       : this.$route.params.id,
            including: ['transactions', 'items', 'effected_at', 'tracking_number', 'shipping_details', 'total_price', 'payable_amount', 'delivery_price', 'currency'],
        };
        return this.$services[this.fetchOrderDetailsService.name][this.fetchOrderDetailsService.api](params).then((response) => {
            this.orderDetails = response.data.results;
        }).catch((error: object) => {
            this.handleError(error);
        }).finally(() => {
            this.$wait.end('fetch-order-details');
        });
    }

    /**
     * Repayment Order
     * 
     * @param {object} order
     * @param {string} callbackRouteName - route name that redirect to it, after successful or unsuccessful payment
     * @returns {Promise}
     */
    repaymentOrder(order: {[k: string]: any}, callbackRouteName: 'payment'): any {
        this.$wait.start(`repayment-action-${order.id}`);
        const callbackRoute = this.$router.resolve({ name: callbackRouteName, params: { id: order.id } }).href;
        const baseUrl = window.location.origin;

        const paramsInternal = {
            id          : order.id,
            callback_url: baseUrl + callbackRoute,
        };

        return this.$services.Cart.paymentTransactionMake(paramsInternal).then((response) => {
            window.location = response.data.results.url;
        }).catch((error: object) => {
            this.handleError(error);
        }).finally(() => {
            this.$wait.end(`repayment-action-${order.id}`);
        });
    }

    /**
     * Fetch Order Details
     * 
     * @param {boolean} val
     */
    @Watch('isDetails', { immediate: true, deep: true })
    onDetailsMode(val: boolean) {
        if (val) {
            this.fetchOrderDetails();
        } else {
            this.fetchOrders();
        }
    }

    /**
     * Order Details Route
     *
     * @param {object} order
     * @returns {object}
     */
    orderDetailsLink(order: {[k: string]: any}): object {
        return {
            name  : this.getValue('detailsLinkName'),
            params: {
                id  : order.id,
                date: order.effected_at,
            },
        };
    }

    /**
     * Set selected filters
     *
     * @param {string} filter
     */
    filterItems(filter: string) {
        this.selectedFilter = filter;
    }
}
